/**
 * @category LeftSide
 * @module Files/fileProcessing/processingLogic
 * @description This file is a list of functions that provide essential functionality to file processing and
 * loading.
 */
import {
  anyUnitToCm1,
  cm1ToAnyUnit,
  hasProperty,
  convertDataFromUnitsToUnits
} from "../../../../utils/helpers";

import units from "../../../../utils/units";


/**
 * This function returns a promise that resolves to a string containing the contents of the file. If the file
 * could not be read, it will generate a warning object, set the warnings, set the new warning count and
 * console log the error.
 * @function
 * @param {file} file - This is a file that will be read.
 * @param {Function} generateWarningObject - This function generates a warning object.
 * @param {Function} setWarnings - This function sets the warnings. Needed for generateWarningObject.
 * @param {Function} setNewWarningCount - This function sets the new warning count. Needed for generateWarningObject.
 * @returns {Promise<string>} Returns a promise that resolves to a string containing the contents of the file.
 */
export function readFile(
  file,
  generateWarningObject,
  setWarnings,
  setNewWarningCount,
  toast = () => {},
  recordedErrorLog = () => {}
) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const fileContent = event.target.result;

        resolve(fileContent);
      } catch (error) {
        reader.abort();
        toast("File processing error.");
        generateWarningObject(
          `File ${
            file && hasProperty(file, "name")
              ? file.name
              : "'Could-not-retrieve-the-name'"
          } could not be processed`,
          2,
          setWarnings,
          setNewWarningCount
        );
        recordedErrorLog("THERE WAS AN ERROR READING A FILE: ", error);
      }
    };
    reader.onerror = () => {
      reader.abort();
      reject(new Error("Error reading the file."));
    };
    reader.readAsText(file);
  });
}

function createLineRegex(colCount, separatorString) {
  // The base pattern for a column (matches a number that might be negative or decimal, with dot or comma)
  const numberPattern = `-?\\d+([\\.,]?\\d+)?([eE][+-]?\\d+)?`;

  // For all separators, allow arbitrary whitespace before and after
  const sepWithWhitespace = `\\s*${separatorString}\\s*`;

  // Allow for leading whitespace with \\s*
  let regexPattern = `^\\s*${numberPattern}`;
  for (let i = 1; i < colCount; i++) {
    regexPattern += `${sepWithWhitespace}${numberPattern}`;
  }

  // Allow arbitrary whitespace at the end of the line
  regexPattern += `\\s*(\\r|\\\r)?$`;

  return regexPattern;
}

export function shiftAndCutDataPoints(
  data,
  options,
  recordedErrorLog = () => {}
) {
  try {
    if (
      options.cut &&
      options.reduceFactor &&
      !isNaN(parseInt(options.reduceFactor)) &&
      parseInt(options.reduceFactor) > 1
    ) {
      const factor = parseInt(options.reduceFactor);
      data = data.filter(
        (_, idx) => idx % factor === 0 || idx === data.length - 1
      );
    }
    if (options.shift) {
      data = data.map((coords) => {
        let xVal = coords.x;
        let yVal = coords.y;
        const xShiftingVal = parseFloat(options.xShiftVal);
        const yShiftingVal = parseFloat(options.yShiftVal);

        // Convert xVal to shift unit for comparison with shift value
        xVal = cm1ToAnyUnit(options.xShiftUnit, xVal);

        switch (options.xShift) {
          case "val-x":
            xVal = xShiftingVal - xVal;
            break;
          case "x+val":
            xVal = xVal + xShiftingVal;
            break;
          case "x*val":
            xVal = xShiftingVal * xVal;
            break;
          default: // if set to "none"
            break;
        }
        xVal = anyUnitToCm1(options.xShiftUnit, xVal);

        switch (options.yShift) {
          case "val-x":
            yVal = yShiftingVal - yVal;
            break;
          case "x+val":
            yVal = yVal + yShiftingVal;
            break;
          case "x*val":
            yVal = yShiftingVal * yVal;
            break;
          default: // if set to "none"
            break;
        }

        // Convert back to cm^-1 for storage
        return {
          x: xVal,
          y: yVal,
        };
      });
    }
    // Cut option: reject if xVal < xMin
    if (
      options.cut &&
      (options.xMin !== "" || options.xMax !== "") &&
      (!isNaN(parseFloat(options.xMin)) || !isNaN(parseFloat(options.xMax)))
    ) {
      data = data.filter((coords) => {
        let xVal = coords.x;
        xVal = cm1ToAnyUnit(options.cutUnit, xVal);
        if (options.xMin !== "" && !isNaN(parseFloat(options.xMin))) {
          const xMinVal = parseFloat(options.xMin);
          if (xVal < xMinVal) {
            return null;
          }
        }
        if (options.xMax !== "" && !isNaN(parseFloat(options.xMax))) {
          const xMaxVal = parseFloat(options.xMax);
          if (xVal > xMaxVal) {
            return null;
          }
        }
        return coords;
      });
    }
    return data;
  } catch (e) {
    recordedErrorLog("ERROR SHIFTING AND CUTTING DATA POINTS: ", e);
    return null;
  }
}

/**
 * This function takes in file content as a string and option object and returns a list of data points
 * generated from them.
 * @function
 * @param {string} fileContent - This is a string containing the contents of the file.
 * @param {object} options - This is an object containing the options for processing the file.
 * @returns {object[]} Returns a list of data points generated from the file content and options.
 */
export function processFileWithOptions(
  fileContent,
  options,
  recordedErrorLog = () => {}
) {
  try {
    let separatorReg;
    switch (options.separator) {
      case "whitespace":
        separatorReg = /\s+/;
        break;
      case "comma":
        separatorReg = /,/;
        break;
      case "semicolon":
        separatorReg = /;/;
        break;
      case "colon":
        separatorReg = /:/;
        break;
      default:
        separatorReg = /\s+/;
    }

    let contentToUse = fileContent;
    let lineRegex = createLineRegex(options.colCount, separatorReg.source);

    if (!options.autoRemove) {
      const lines = contentToUse.split("\n");
      contentToUse = lines.slice(options.manualLineStart).join("\n");
    }

    const dataPointPattern = new RegExp(lineRegex);

    // Debug logging

    if (options.autoRemove) {
      const lines = fileContent.split("\n");
      // Find the index of the first line that looks like a data point
      const firstDataLineIndex = lines.findIndex((line) => {
        return dataPointPattern.test(line);
      });
      // If no data point line was found, return an empty string
      if (firstDataLineIndex === -1) {
        return null;
      }
      // Join all the lines after the first data point line into a single string
      contentToUse = lines.slice(firstDataLineIndex).join("\n");
    }

    let data = contentToUse
      .trim()
      .split("\n")
      .map((line) => {
        const allColumns = line.trim().split(separatorReg);
        // Skip lines where the column count does not match
        if (allColumns.length !== options.colCount) {
          return null;
        }
        const xColNum = options.xColNum;
        const yColNum = options.yColNum;
        //const eColNum = options.eColNum;

        // Replace comma with period for decimal separator support
        const parseNumber = (str) => {
          if (str === undefined || str === null) {
            return NaN;
          }
          return parseFloat(String(str).replace(",", "."));
        };

        let xVal = parseNumber(allColumns[xColNum - 1]);
        let yVal = parseNumber(allColumns[yColNum - 1]);

        // Skip line if x or y is not a valid number
        if (Number.isNaN(xVal) || Number.isNaN(yVal)) {
          return null;
        }

        //if (eColNum === "none") {
        return {
          x: xVal,
          y: yVal,
        };
        // } else {
        //   // Also check eColNum if present
        //   let eVal = parseNumber(allColumns[eColNum - 1]);
        //   if (Number.isNaN(eVal)) {
        //     return null;
        //   }
        //   return {
        //     x: xVal,
        //     y: yVal,
        //     e: eVal,
        //   };
        // }
      })
      .filter((coords) => {
        // Filter out nulls from skipped lines
        if (!coords) return false;
        if (hasProperty(coords, "e")) {
          return (
            coords.x != null &&
            !Number.isNaN(coords.x) &&
            coords.e != null &&
            !Number.isNaN(coords.e) &&
            coords.y != null &&
            !Number.isNaN(coords.y)
          );
        } else {
          return (
            coords.x != null &&
            !Number.isNaN(coords.x) &&
            coords.y != null &&
            !Number.isNaN(coords.y)
          );
        }
      });

    if (data.length > 0) {
      // Apply reduction factor if set and valid
      data = convertDataFromUnitsToUnits(options.xUnit, units[0], data);
      data = shiftAndCutDataPoints(data, options, recordedErrorLog);

      return data;
    } else {
      return null;
    }
  } catch (e) {
    recordedErrorLog("ERROR PARSING FILE DATA WITH OPTIONS: ", e);
    return null;
  }
}

/**
 * This function takes in an array of data points and changes them according to provided options
 * @function
 * @param {Array.<{x: number, y: number}>} dataPoints - This is an array of points that we want to change with options.
 * @param {Object} options - This is an object containing the options for processing the file.
 * @returns {Array.<{x: number, y: number}>} Returns a list of data points updated with options.
 */
export function dataPointsUpdateWithOptions(
  dataPoints,
  options,
  recordedErrorLog = () => {}
) {
  const dataPointsConverted = dataPoints.map((coords) => {
    let xaxisValue = anyUnitToCm1(options.xUnit, coords.x);

    return { ...coords, x: xaxisValue };
  });
  const data = shiftAndCutDataPoints(
    dataPointsConverted,
    options,
    recordedErrorLog
  );
  return data;
}

/**
 * This function takes in an array of data points reverts them to original units from cm-1
 * @function
 * @param {Array.<{x: number, y: number}>} dataPoints - This is an array of points that we want to change with options.
 * @param {Object} originalUnit - This is an object describing units we want to revert to.
 * @returns {Array.<{x: number, y: number}>} Returns a list of data points reverted to original units.
 */
export function revertDataPointsToOriginalUnits(
  dataPoints,
  originalUnit,
  recordedErrorLog = () => {}
) {
  try {
    const data = dataPoints.map((dataPoint) => {
      let xaxisValue = cm1ToAnyUnit(originalUnit, dataPoint.x);

      return { ...dataPoint, x: xaxisValue };
    });

    if (data.length > 0) {
      return data;
    } else {
      return null;
    }
  } catch (e) {
    recordedErrorLog("ERROR REVERTING DATA POINTS TO ORIGINAL UNITS: ", e);
    return null;
  }
}

/**
 * This function checks if file was marked as solved.
 * @function
 * @param {file} fileContent - This is a file that we want to check if it was solved already.
 * @returns {boolean} Returns true if file was marked as solved, false otherwise.
 */
export function checkIfSolved(file) {
  if (hasProperty(file, "solved") && file.solved) {
    return true;
  } else {
    return false;
  }
}

/**
 * This function copies an array of files with no references kept.
 * @function
 * @param {Array.<file>} fileContent - This is a file array that we want to copy.
 * @param {Function} recordedErrorLog - Optional error logging function from GeneralContext
 * @returns {Array.<file>} This is a copy of file array that no longer has reference to old array.
 */
export function deepCopyFiles(files, recordedErrorLog = () => {}) {
  // const newFiles = deepCopy(files);
  let newFiles = [];
  try {
    newFiles = structuredClone(files);
  } catch (e) {
    recordedErrorLog("STRUCTURED CLONE FAILED: ", e, true, "Failed to load files. Try updating your browser.");
    return newFiles;
  }

  for (let i = 0; i < files.length; i++) {
    const oldFile = files[i];

    if (hasProperty(oldFile, "solved")) {
      newFiles[i].solved = oldFile.solved;
    }
  }

  return newFiles;
}

/**
 * This function finds next available index of unsolved file.
 * @function
 * @param {Array.<object>} fileList - Array of objects that contain information about files being processed.
 * @returns {number} Returns an index of next unsolved file, if all the files are solved, returns null.
 */
export function getNextAvailableIndex(fileList) {
  let newIndex = null;

  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i];

    if (!hasProperty(file, "solved") || !file.solved) {
      newIndex = i;
      break;
    }
  }

  return newIndex;
}
